import { inject, injectable } from 'inversify'
import type { Capability } from '@shared'
import type { FilterScope } from '@core/infra/filters/filters.config'
import { BooksRepository } from '@features/books/books.repository'
import { GamesListRepository } from '@features/games/games-list.repository'
import { GameRepository } from '@features/game/game.repository'
import { InfraDemoRepository } from '@features/infra-demo/infra-demo.repository'
import { AdminUsersRepository } from '@core/infra/authentication/admin/admin-users.repository'
import { AdminErrorLookupRepository } from '@core/infra/observability/errors/admin-error-lookup.repository'
import { AdminAuditRepository } from '@core/infra/observability/audit/admin-audit.repository'

export const PublicRoutes = ['Login', 'NotFound'] as const

export const PrivateRoutes = [
  'Home',
  'Lobby',
  'Game.Setup',
  'Game.Play',
  'HR.Users',
  'HR.User',
  'Admin.Errors',
  'Admin.Users',
  'Admin.Audit',
] as const

export const TestRoutes = [
  // Books and Infra Demo are seed-only fixture surface — kept in the suite
  // for what they exercise (a real end-to-end feature, and a page composing
  // several independent capability sections) but with nothing a real user
  // should reach in production. Gated behind `Config.showDemoSurface`, same
  // as the clickable/expandable/columns tree below.
  'Books',

  'InvisiblePage',
  'UnClickablePage',
  'ClickablePage',
  'ExpandablePage',
  'ExpandablePageChild1',
  'ExpandablePageChild2',
  'BothPage',
  'BothPageChild1',
  'BothPageChild2',
  'BothPageBoth',
  'BothPageBothChild1',
  'BothPageBothChild2',
  'ColumnsPage',
  'ColumnsPageSettings',
  'ColumnsPageAdvanced',
  'ColumnsPageAdvancedOptionA',
  'ColumnsPageAdvancedOptionB',
  'InfraDemoPage',
] as const

export const RouteIds = [...PublicRoutes, ...PrivateRoutes, ...TestRoutes] as const

export type RouteId = (typeof RouteIds)[number]

export type Shell = 'bare' | 'app'

export type RouteConfigEntry = {
  routeId: RouteId
  routeDef: {
    path: string
    isSecure: boolean
    shell: Shell
    /**
     * Translation key for the browser tab title. Optional: a route without one
     * falls back to PageTitleConfig.fallbackTitleKey, so a new route is never
     * silently titleless.
     */
    titleKey?: string
    /**
     * Display-side gate only: the Router redirects a session without this
     * role/capability to Home (router.ts planTarget). The backend re-checks
     * on every request — this exists so a non-admin typing an admin URL
     * lands somewhere sensible instead of on a page of 403s.
     */
    requiresCapability?: Capability
    /**
     * Gated behind the global admin-mode switch (spec/30-structure.md), on
     * top of whatever `requiresCapability` already requires. Display-side
     * only, same caveat as `requiresCapability`: the backend does not know or
     * care that this switch exists.
     */
    requiresAdminMode?: true
    /**
     * Fixture routes — Books, Infra Demo, and the whole clickable/expandable/
     * columns tree under HR — kept in the suite for what they exercise (a
     * real end-to-end feature, deep nav trees, column layouts) but with
     * nothing a real user should reach in production. Gated the same way as
     * `requiresAdminMode`, off `Config.showDemoSurface` rather than a
     * capability: nobody is meant to have this, ever, in prod.
     */
    requiresDemoSurface?: true
  }
  /**
   * Widened to carry the route's own resolved `:param` values — the params
   * a param-carrying route needs to load its own data. Every existing
   * zero-arg handler still typechecks unchanged.
   */
  onEnter?: (params: Readonly<Record<string, string>>) => Promise<void>
  onLeave?: () => Promise<void>

  /**
   * The list this route shows, when it has one. Declared here because the URL is
   * the route's own surface: it is what makes a narrowed list refreshable,
   * bookmarkable and shareable, and FilterUrlService reads this one table rather
   * than each page remembering to save itself.
   */
  filterScope?: FilterScope | ((query: Readonly<Record<string, string>>) => FilterScope)
}

@injectable()
export class RoutesConfig {
  // Repositories, not Presenters: this instance lives for the whole app and
  // is never disposed. A Repository is always Singleton — safe to hold
  // permanently by construction. A Presenter's scope is Transient by default
  // (container.base.ts) — holding one here as a permanent field would make
  // it behave like an undeclared Singleton. Repositories can't write
  // feedback (P-40), so a route-entry load failure here is currently silent.
  @inject(BooksRepository)
  private booksRepository!: BooksRepository

  @inject(GamesListRepository)
  private gamesListRepository!: GamesListRepository

  @inject(GameRepository)
  private gameRepository!: GameRepository

  @inject(InfraDemoRepository)
  private infraDemoRepository!: InfraDemoRepository

  @inject(AdminUsersRepository)
  private adminUsersRepository!: AdminUsersRepository

  @inject(AdminErrorLookupRepository)
  private adminErrorLookupRepository!: AdminErrorLookupRepository

  @inject(AdminAuditRepository)
  private adminAuditRepository!: AdminAuditRepository

  routes: RouteConfigEntry[] = [
    {
      routeId: 'Books',
      routeDef: {
        path: '/app/books',
        isSecure: true,
        shell: 'app',
        titleKey: 'nav.books',
        requiresDemoSurface: true,
      },
      onEnter: async () => {
        // Not awaited on purpose — the route commits immediately and the
        // page shows its own loading state while the fetch is in flight.
        // loadIfNeeded, not load: returning to a page already loaded should
        // reuse that data, not re-fetch it behind a skeleton.
        this.booksRepository.loadIfNeeded()
      },
      // No onLeave: leaving a route is not a reason to throw its data away.
    },
    {
      routeId: 'HR.Users',
      routeDef: {
        path: '/hr/users',
        isSecure: true,
        shell: 'app',
        titleKey: 'nav.users',
        requiresDemoSurface: true,
      },
    },
    {
      routeId: 'HR.User',
      routeDef: {
        path: '/hr/users/:userId',
        isSecure: true,
        shell: 'app',
        titleKey: 'nav.users',
        requiresDemoSurface: true,
      },
    },
    // Auth & Home
    {
      routeId: 'Login',
      routeDef: {
        path: 'login',
        isSecure: false,
        shell: 'bare',
        titleKey: 'auth.login',
      },
    },
    {
      routeId: 'Home',
      routeDef: {
        path: '/',
        isSecure: true,
        shell: 'app',
        titleKey: 'nav.games',
      },
      onEnter: async () => {
        this.gamesListRepository.loadIfNeeded()
      },
    },
    {
      routeId: 'Lobby',
      routeDef: {
        path: '/lobby',
        isSecure: true,
        shell: 'app',
        titleKey: 'lobby.title',
      },
      // No preload: a fresh LobbyPm has nothing to fetch until the player
      // creates or joins a game — unlike Home/Books there is no "my current
      // state" for the server to hand back on arrival.
    },
    {
      routeId: 'Game.Setup',
      routeDef: {
        path: '/game/:gameId/setup',
        isSecure: true,
        shell: 'app',
        titleKey: 'game.setup.title',
      },
      onEnter: async ({ gameId }) => {
        // Fire-and-forget, like every other preload: the page renders its
        // own loading state off `GameRepository.loadState`.
        this.gameRepository.load(gameId)
      },
      // Unlike a list, a game is live state behind a 3s opponent poll —
      // leaving the screen must stop it, or the poll outlives the route.
      onLeave: async () => {
        this.gameRepository.leaveGame()
      },
    },
    {
      routeId: 'Game.Play',
      routeDef: {
        path: '/game/:gameId/play',
        isSecure: true,
        shell: 'app',
        titleKey: 'game.play.title',
      },
      // Same shape as Game.Setup: `load` is idempotent-by-gameId (re-fetches
      // regardless of what Game.Setup's own onLeave already tore down), and
      // a hard reload of /game/:gameId/play works standalone the same way.
      onEnter: async ({ gameId }) => {
        this.gameRepository.load(gameId)
      },
      onLeave: async () => {
        this.gameRepository.leaveGame()
      },
    },
    {
      routeId: 'Admin.Errors',
      routeDef: {
        path: '/app/admin/errors',
        isSecure: true,
        shell: 'app',
        titleKey: 'nav.adminErrors',
        requiresCapability: 'errors.read',
        requiresAdminMode: true,
      },
      onEnter: async () => {
        this.adminErrorLookupRepository.loadIfNeeded()
      },
    },
    {
      routeId: 'Admin.Users',
      filterScope: 'admin.users',
      routeDef: {
        path: '/app/admin/users',
        isSecure: true,
        shell: 'app',
        titleKey: 'nav.adminUsers',
        requiresCapability: 'users.manage',
        requiresAdminMode: true,
      },
      onEnter: async () => {
        this.adminUsersRepository.loadIfNeeded()
      },
    },
    {
      routeId: 'Admin.Audit',
      filterScope: 'admin.audit',
      routeDef: {
        path: '/app/admin/audit',
        isSecure: true,
        shell: 'app',
        titleKey: 'nav.adminAudit',
        requiresCapability: 'audit.read',
        requiresAdminMode: true,
      },
      onEnter: async () => {
        this.adminAuditRepository.loadIfNeeded()
      },
    },
    // Demo pages for the navigation clickability showcase
    {
      routeId: 'InvisiblePage',
      routeDef: { path: '/demo/invisible', isSecure: true, shell: 'app', requiresDemoSurface: true },
    },
    {
      routeId: 'UnClickablePage',
      routeDef: { path: '/demo/unclickable', isSecure: true, shell: 'app', requiresDemoSurface: true },
    },
    {
      routeId: 'ClickablePage',
      routeDef: { path: '/demo/clickable', isSecure: true, shell: 'app', requiresDemoSurface: true },
    },
    {
      routeId: 'ExpandablePage',
      routeDef: { path: '/demo/expandable', isSecure: true, shell: 'app', requiresDemoSurface: true },
    },
    {
      routeId: 'ExpandablePageChild1',
      routeDef: { path: '/demo/expandable/child1', isSecure: true, shell: 'app', requiresDemoSurface: true },
    },
    {
      routeId: 'ExpandablePageChild2',
      routeDef: { path: '/demo/expandable/child2', isSecure: true, shell: 'app', requiresDemoSurface: true },
    },
    {
      routeId: 'BothPage',
      routeDef: { path: '/demo/both', isSecure: true, shell: 'app', requiresDemoSurface: true },
    },
    {
      routeId: 'BothPageChild1',
      routeDef: { path: '/demo/both/child1', isSecure: true, shell: 'app', requiresDemoSurface: true },
    },
    {
      routeId: 'BothPageChild2',
      routeDef: { path: '/demo/both/child2', isSecure: true, shell: 'app', requiresDemoSurface: true },
    },
    {
      routeId: 'BothPageBoth',
      routeDef: { path: '/demo/both/both', isSecure: true, shell: 'app', requiresDemoSurface: true },
    },
    {
      routeId: 'BothPageBothChild1',
      routeDef: { path: '/demo/both/both/child1', isSecure: true, shell: 'app', requiresDemoSurface: true },
    },
    {
      routeId: 'BothPageBothChild2',
      routeDef: { path: '/demo/both/both/child2', isSecure: true, shell: 'app', requiresDemoSurface: true },
    },
    {
      routeId: 'ColumnsPage',
      routeDef: { path: '/demo/columns', isSecure: true, shell: 'app', requiresDemoSurface: true },
    },
    {
      routeId: 'ColumnsPageSettings',
      routeDef: { path: '/demo/columns/settings', isSecure: true, shell: 'app', requiresDemoSurface: true },
      onEnter: async () => {
        await new Promise<void>((resolve) => setTimeout(resolve, 1_000))
      },
    },
    {
      routeId: 'ColumnsPageAdvanced',
      routeDef: { path: '/demo/columns/advanced', isSecure: true, shell: 'app', requiresDemoSurface: true },
    },
    {
      routeId: 'ColumnsPageAdvancedOptionA',
      routeDef: { path: '/demo/columns/advanced/option-a', isSecure: true, shell: 'app', requiresDemoSurface: true },
    },
    {
      routeId: 'ColumnsPageAdvancedOptionB',
      routeDef: { path: '/demo/columns/advanced/option-b', isSecure: true, shell: 'app', requiresDemoSurface: true },
    },
    {
      routeId: 'InfraDemoPage',
      routeDef: {
        path: '/demo/infra',
        isSecure: true,
        shell: 'app',
        titleKey: 'nav.infraDemo',
        requiresDemoSurface: true,
      },
      // Synchronous and cheap (one storage read), so unlike Books this is
      // awaited — the page renders with the note already in place.
      onEnter: async () => {
        this.infraDemoRepository.restore()
      },
    },
    // Catch-all: unknown URLs land here (honest 404)
    {
      routeId: 'NotFound',
      routeDef: {
        titleKey: 'notFound.title',
        path: '*',
        isSecure: false,
        shell: 'bare',
      },
    },
  ]
}
